import React from "react";
import HomeSlider from "./HomeSlider";
import DimensionsService from "services/DimensionsService"
import SettingsService from "services/SettingsService"
import DimensionInfo from "models/DimensionInfo"
import DimensionsContainer from "components/DimensionsContainer"
import HeaderMobile from "components/header/HeaderMobile"
import NativeInterface from "services/NativeInterface"
import AuthContext from "store/AuthContext";
import Geolocation from "../../models/Geolocation"
import GeolocationService from "services/GeolocationService";
import { useNavigate } from 'react-router-dom';

import "./MainPage.css"

function getAccurateDistance(a: {lat: number, lng: number}, b: {lat: number, lng: number}) {
    const toRad = (v: number) => v * Math.PI / 180;
    const R = 6371e3; // metres
    const φ1 = toRad(a.lat);
    const φ2 = toRad(b.lat);
    const Δφ = toRad(b.lat - a.lat);
    const Δλ = toRad(b.lng - a.lng);
    // Haversine formula
    const sinΔφ = Math.sin(Δφ / 2);
    const sinΔλ = Math.sin(Δλ / 2);
    const aVal = sinΔφ * sinΔφ + Math.cos(φ1) * Math.cos(φ2) * sinΔλ * sinΔλ;
    const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
    return R * c;
}

function getApproxDistanceSq(a: {lat: number, lng: number}, b: {lat: number, lng: number}) {
    // Approximate: 1 degree latitude ≈ 111km, longitude scaled by cos(latitude)
    const latDiff = a.lat - b.lat;
    const lngDiff = (a.lng - b.lng) * Math.cos((a.lat + b.lat) * Math.PI / 360);
    return latDiff * latDiff + lngDiff * lngDiff;
}

function sortDimensions(dimensions: DimensionInfo[], location: Geolocation): DimensionInfo[] {
    if (!location) {
        return dimensions;
    }

    const distances: {[key: string]: number} = {};
    dimensions.forEach((dim) => {
        if (dim.region?.point) {
            distances[dim.id] = getApproxDistanceSq(
                { lat: dim.region.point.lat, lng: dim.region.point.lng },
                { lat: location.latitude, lng: location.longitude }
            );
        }
    });

    return dimensions.slice().sort((a, b) => {
        const distA = distances[a.id] || 100000000;
        const distB = distances[b.id] || 100000000;
        return distA - distB;
    });
}

function sortKarmielFirst(dimensions: DimensionInfo[]) {
    return dimensions.slice().sort((a, b) => {
        if (a.id == '3382710084') {
            return -1; // Karmiel first
        }
        return a.id < b.id ? -1 : (a.id > b.id ? 1 : 0);
    });
}

const MainPage: React.FC = () => {
    const authCtx = React.useContext(AuthContext);
    const [dimensions, setDimensions] = React.useState<DimensionInfo[]>([]);
    const [location, setLocation] = React.useState<Geolocation | null>(null);
    //const [location, setLocation] = React.useState<Geolocation | null>(new Geolocation('32.91661429013812 35.29199638484971 0 0 0')); // Karmiel

    const getHomeDimension = () => {
        const HOME_DIMENSION_ID = 'HOME_DIMENSION_ID';

        if (SettingsService.homeDimensionOverride()) {
            return SettingsService.homeDimensionOverride();
        }

        if (!location || dimensions.length == 0) {
            const storedId = window.localStorage.getItem(HOME_DIMENSION_ID);
            return storedId || '';
        }

        const sorted = sortDimensions(dimensions, location!);

        const dim = sorted[0];
        if (!dim.region) {
            return '';
        }
/*
        const dist = getAccurateDistance(
            { lat: dim.region.point.lat, lng: dim.region.point.lng },
            { lat: location.latitude, lng: location.longitude }
        );

        if (dist > dim.region.radius) {
            return '';
        }
*/
        window.localStorage.setItem(HOME_DIMENSION_ID, dim.id);

        return dim.id;
    };


    const [homeDimension, setHomeDimension] = React.useState<string>(getHomeDimension());
    const navigate = useNavigate();

    React.useEffect(() => {
        let ids = authCtx.account.getAllDimensions();

        GeolocationService.subscribeOnUpdate(setLocation);

        DimensionsService.listDimensions({public: true, ids: ids})
            .then((data: DimensionInfo[]) => {
                let sortedData = location ? sortDimensions(data, location!) : sortKarmielFirst(data);
                if (location && sortedData.length > 0 && sortedData[0].id == '3382710084') {
                    sortedData = [sortedData[0]]
                }
                setDimensions(sortedData);
            })
            .catch(error => console.error(`List dimensions error: ${error}`));

        return () => {
            GeolocationService.unsubscribeOnUpdate(setLocation);
        };
    }, [authCtx]);

    React.useEffect(() => {
        if (location) {
            setDimensions(prev => sortDimensions(prev, location!));
        }
        setHomeDimension(getHomeDimension());
    }, [location, dimensions]);

    React.useEffect(() => {
        const hasNavigated = sessionStorage.getItem("mainPageHasNavigated");
        if (NativeInterface.isPWA() && homeDimension && !hasNavigated) {
            navigate(`/dimension?dim=${homeDimension}`);
            sessionStorage.setItem("mainPageHasNavigated", "true");
        }
    }, [homeDimension]);

    return (
        <div className="main-page-container">
            <div className={NativeInterface.isPWA() ? '' : 'small-screen-only'}>
                <HeaderMobile />
            </div>
            <HomeSlider dimension={homeDimension} />
            <div className="main-page-dimensions">
                <DimensionsContainer dimensions={dimensions}/>
            </div>
        </div>
    );
};

export default MainPage;
